import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { BRAND, UI } from "@/content";
import { useLang } from "@/LangContext";

function Row({ label, value, testid, href, icon: Icon, external, accent }) {
  const inner = (
    <span className="flex items-center justify-between gap-4 py-4 border-b border-gold/15 group/row w-full">
      <span className="flex items-center gap-3 text-cream">
        <Icon className="w-4 h-4 text-gold shrink-0" />
        {label}
      </span>
      <span
        className={`font-mono text-sm text-right ${
          accent || "text-sage group-hover/row:text-cream transition-colors duration-300"
        }`}
      >
        {value}
      </span>
    </span>
  );
  return href ? (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      data-testid={testid}
      className="block"
    >
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}

export default function Contact() {
  const { lang } = useLang();
  const t = UI[lang] || UI.es;

  return (
    <section
      id="contacto"
      className="relative py-24 lg:py-28 scroll-mt-16"
      data-testid="contact-section"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-goldbright flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-9 bg-gold/70 inline-block" />
            {t.contact.eyebrow}
            <span className="h-px w-9 bg-gold/70 inline-block" />
          </p>
          <h2 className="font-serif font-medium text-4xl sm:text-5xl text-cream leading-[1.08]">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-sage font-light leading-relaxed">{t.contact.sub}</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl border border-gold/20 bg-moss p-8 sm:p-10 flex flex-col"
            data-testid="contact-card"
          >
            <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-goldbright mb-2">
              {t.contact.hoursTitle}
            </p>
            <Row label={t.contact.days} value={t.contact.hoursVal} testid="contact-hours-weekdays" icon={Clock} />
            <Row label={t.contact.sunday} value={t.contact.closed} testid="contact-hours-sunday" icon={Clock} accent="text-sage" />
            <div className="mt-6">
              <Row label={t.contact.addressLabel} value="Carrer de la Constitució 24 · Sants" testid="contact-address-row" icon={MapPin} />
              <Row
                label={t.contact.call}
                value={BRAND.phoneDisplay}
                testid="contact-phone-button"
                href={BRAND.phoneHref}
                icon={Phone}
                accent="text-cream font-medium"
              />
              <Row
                label={t.contact.emailLabel}
                value={BRAND.email}
                testid="contact-email-button"
                href={`mailto:${BRAND.email}`}
                icon={Mail}
              />
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              <a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-wamist/50 text-wamist px-6 py-4 text-[15px] font-medium hover:bg-wamist/10 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
              >
                <MessageCircle className="w-[18px] h-[18px]" />
                {t.contact.wa}
              </a>
              <a
                href={BRAND.fresha}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-fresha-button"
                className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-gold px-6 py-4 font-medium text-forest hover:bg-goldbright hover:scale-[1.02] active:scale-[0.98] transition-transform duration-300"
              >
                {t.contact.book}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
            <p className="mt-4 text-xs text-sage/80 text-center">{t.contact.waNote}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-2xl border border-gold/20 overflow-hidden min-h-[420px]"
            data-testid="contact-map"
          >
            <iframe
              title="Royale — Carrer de la Constitució 24, Barcelona"
              src={BRAND.mapsEmbed}
              loading="lazy"
              className="absolute inset-0 w-full h-full grayscale-[0.35] contrast-[1.05]"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute bottom-4 left-4 right-4 sm:right-auto flex items-center justify-between gap-4 rounded-xl bg-forest/90 backdrop-blur border border-gold/25 px-5 py-4">
              <span className="flex items-center gap-3 text-sm text-cream">
                <MapPin className="w-4 h-4 text-gold shrink-0" />
                {BRAND.address}
              </span>
              <a
                href={BRAND.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-maps-link"
                className="font-mono text-[10px] tracking-[0.2em] uppercase text-goldbright hover:text-gold whitespace-nowrap transition-colors duration-300"
              >
                {t.contact.directions}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
